import React from "react";
import { ExternalLink, ShoppingBag } from "lucide-react";
import ProductMedia from "@/components/shop/ProductMedia";

/**
 * Product tile. The whole tile is an external anchor pointing at the
 * Shift4Shop store URL so every click routes the researcher straight to the
 * secure checkout flow on the connected store.
 */
export default function ProductCard({ product }) {
  return (
    <a
      href={product.store_url}
      className="group block bg-white rounded-sm border border-transparent hover:border-[#C8A96A]/50 transition-colors duration-500 shadow-[0_8px_30px_rgba(30,42,68,0.05)] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8A96A]"
      data-testid={`product-card-${product.slug}`}
      aria-label={`Buy ${product.name} on our secure store`}
    >
      <div className="relative block w-full overflow-hidden">
        <ProductMedia product={product} className="h-56 w-full" />
        {product.featured && (
          <span className="absolute top-3 left-3 z-10 bg-[#C8A96A] text-[#111C32] text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-sm">
            Featured
          </span>
        )}
        <span
          className={`absolute top-3 right-3 z-10 text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-sm ${
            product.availability === "in-stock" ? "bg-white/90 text-[#1E2A44]" : "bg-[#85827D] text-white"
          }`}
        >
          {product.availability === "in-stock" ? "In Stock" : "Unavailable"}
        </span>
        {/* hover buy affordance */}
        <span className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 bg-[#1E2A44]/92 text-white text-xs font-medium py-2.5 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out">
          <ShoppingBag size={14} className="text-[#D8BE87]" /> Buy on Store
        </span>
      </div>

      <div className="p-6">
        <p className="overline mb-2">{product.collection_name}</p>
        <div className="font-serif text-2xl text-[#1E2A44] leading-tight">{product.name}</div>
        <p className="mt-1 text-sm text-[#85827D]">{product.presentation}</p>

        <div className="mt-5 flex items-center justify-between">
          {product.price != null ? (
            <span className="text-lg font-semibold text-[#1E2A44]" data-testid={`price-${product.slug}`}>
              ${Number(product.price).toFixed(2)}
            </span>
          ) : (
            <span className="text-xs text-[#85827D] inline-flex items-center gap-1">
              View live pricing <ExternalLink size={12} className="text-[#C8A96A]" />
            </span>
          )}
          <span
            className="inline-flex items-center gap-1.5 bg-[#1E2A44] text-white text-xs font-medium px-3.5 py-2 rounded-sm group-hover:bg-[#111C32] transition-colors"
            data-testid={`buy-on-store-${product.slug}`}
          >
            Buy <ExternalLink size={13} className="text-[#D8BE87]" />
          </span>
        </div>
      </div>
    </a>
  );
}
